import { generateNKeysBetween } from "fractional-indexing";

import { RemPreferences } from "../../../foundation/api/model";
import ApiBarrel from "../../../foundation/api/model/barrels/Barrel";
import BarrelKind from "../../../foundation/api/model/barrels/BarrelKind";
import ScreenSection from "../../../foundation/api/model/barrels/ScreenSection";
import OrganizationMember from "../../../foundation/api/model/organizations/OrganizationMember";
import {
    ProjectAIOrganizeResultSuggestionNewSection,
    ProjectAIOrganizeResultSuggestionUpdatedSection,
    ProjectAIOrganizeResultSuggestionNewVariantGroup,
    ProjectAIOrganizeResultSuggestionUpdatedVariantGroup,
    ProjectAIOrganizeResultSuggestionItem
} from "../../../foundation/api/model/projectAIOrganize/ProjectAIOrganizeResult";
import { PLATFORM, PlatformContextMap, WorkspaceTabs } from "../../../foundation/enums";
import Barrel from "../../../foundation/model/Barrel";
import BarrelType from "../../../foundation/model/BarrelType";
import BarrelUser from "../../../foundation/model/BarrelUser";
import NoteType from "../../../foundation/model/NoteType";
import StyleguideData from "../../../foundation/model/StyleguideData";
import VariantGroup, { VariantScreen } from "../../../foundation/model/VariantGroup";

import router from "../../../foundation/router";
import { sortArrayOfObjectsByPropertyName } from "../../../foundation/utils/array";
import { getLinkedStyleguideIds, getParentStyleguideToInheritRemPreferences } from "../../../foundation/utils/barrel";
import isUserMemberOfBarrel from "../../../foundation/utils/barrel/isUserMemberOfBarrel";
import BasicRecord from "../../../foundation/utils/BasicRecord";
import { isOrganizationProject } from "../../../foundation/utils/legacy";
import { createTemporarySectionId, TemporarySectionId } from "../../../foundation/utils/section";
import { transformScreenSections } from "../../../foundation/utils/transform";
import { urlResolve } from "../../../foundation/utils/url";

import OrganizationHelpers from "../organization/OrganizationHelpers";

import fluxRuntime from "../fluxRuntime";

// TODO-bedo: Duplicated VariantGroupData declaration to prevent circular dependency, should be fixed later
type VariantGroupData = {
    variantGroupId: string;
    variantGroupName: string;
    variantName: string;
    variants: VariantScreen[];
};

interface NavigateToWorkspaceParams {
    oid?: string | null;
    barrelType?: BarrelType;
    query?: BasicRecord<unknown>;
}

function hasProject(pid: string): boolean {
    const { projects } = fluxRuntime.ProjectsDataStore.getState();

    return projects && pid in projects;
}

function hasStyleguide(stid: string): boolean {
    const { styleguides } = fluxRuntime.StyleguidesDataStore.getState();

    return styleguides && stid in styleguides;
}

function getBarrelAndType({ bid }: {
    bid?: string | null;
} = {}): {
    barrelType: BarrelType;
    barrel: Barrel;
} | null {
    let barrelId = bid;
    if (!barrelId) {
        ({ barrelId } = fluxRuntime.BarrelStore.getState());
    }

    if (!barrelId) {
        return null;
    }

    const { projects } = fluxRuntime.ProjectsDataStore.getState();
    if (projects && barrelId in projects) {
        return {
            barrelType: BarrelType.PROJECT,
            barrel: projects[barrelId]
        };
    }

    const { styleguides } = fluxRuntime.StyleguidesDataStore.getState();
    if (styleguides && barrelId in styleguides) {
        return {
            barrelType: BarrelType.STYLEGUIDE,
            barrel: styleguides[barrelId]
        };
    }

    return null;
}

function getBarrel({ bid }: {
    bid?: string | null;
} = {}): Barrel | null {
    return getBarrelAndType({ bid })?.barrel || null;
}

function getProject({ pid }: {
    pid?: string | null;
} = {}): Barrel | null {
    let projectId = pid;
    if (!projectId) {
        const { barrelId, barrelType } = fluxRuntime.BarrelStore.getState();
        if (barrelType === BarrelType.PROJECT) {
            projectId = barrelId;
        }
    }

    if (!projectId) {
        return null;
    }

    const { projects } = fluxRuntime.ProjectsDataStore.getState();
    if (projects && projectId in projects) {
        return projects[projectId];
    }

    return null;
}

function findUser({ bid, uid }: {
    bid?: string | null;
    uid?: string | null;
} = {}): BarrelUser | OrganizationMember | null | undefined {
    const barrel = getBarrel({ bid });

    if (!barrel) {
        return null;
    }

    let userId = uid;
    if (!userId) {
        ({ userId } = fluxRuntime.UserStore.getState());
    }

    if (isOrganizationProject(barrel)) {
        const { members } = OrganizationHelpers.getOrganization(undefined, { pid: barrel._id }) || {};

        if (members?.length) {
            return members.find(({ user }) => user._id === userId);
        }
    }

    return barrel.users.find(({ user }) => user._id === userId);
}

function canUploadDesigns({ uid, pid }: {uid: string; pid?: string | null;}): boolean {
    const project = getProject({ pid });

    if (!project) {
        return false;
    }

    const currentUser = project.users.find(({ user }) => user._id === uid);

    if (!currentUser) {
        return false;
    }

    if (isOrganizationProject(project)) {
        return ["king", "queen", "rook"].includes(currentUser.role);
    }

    const projectOwner = project.users.find(user => user.role === "owner");
    if (projectOwner) {
        if (projectOwner.user.paymentPlan === "free") {
            return currentUser.role === "owner";
        }

        // Growing or starter plan
        return true;
    }

    return false;
}

function isUserRestricted(): boolean {
    return findUser()?.role === "pawn";
}

function getVariantGroupOfScreen(screenId: string): VariantGroup | undefined {
    const { variantGroups = [] } = getProject() || {};

    return variantGroups.find(({ screens }) => screens.some(({ id }) => id === screenId));
}

function getScreenIdsOfVariantGroup(variantGroupId: string, variantGroups: VariantGroup[]): string[] | undefined {
    return variantGroups.find(({ _id }) => _id === variantGroupId)?.screens.map(({ id }) => id);
}

function getScreenIdsFromAIOrganizedSection(
    section: ProjectAIOrganizeResultSuggestionNewSection | ProjectAIOrganizeResultSuggestionUpdatedSection,
    variantGroups: VariantGroup[]
): string[] {
    return section.items?.flatMap(item => (
        item.type === "variantGroup"
            ? getScreenIdsOfVariantGroup(item.id, variantGroups) || []
            : [item.id]
    )) || [];
}

function getScreenIdsFromAIOrganizedVariantGroup(
    variantGroup: ProjectAIOrganizeResultSuggestionNewVariantGroup |
    ProjectAIOrganizeResultSuggestionUpdatedVariantGroup
): string[] {
    return variantGroup.screens.map(({ id }) => id);
}

function getAIOrganizedProjectSections({
    sections,
    variantGroups,
    newSections,
    updatedSections,
    newVariantGroups,
    updatedVariantGroups
}: {
    sections: ScreenSection[];
    variantGroups: VariantGroup[];
    newSections: ProjectAIOrganizeResultSuggestionNewSection[];
    updatedSections: ProjectAIOrganizeResultSuggestionUpdatedSection[];
    newVariantGroups: ProjectAIOrganizeResultSuggestionNewVariantGroup[];
    updatedVariantGroups: ProjectAIOrganizeResultSuggestionUpdatedVariantGroup[];
}): {
    sections: ScreenSection[];
    variantGroups: VariantGroup[];
    AIOrganizedScreenIds: string[];
    AIOrganizedSectionIds: string[];
    AIGeneratedSectionIds: string[];
    AIOrganizedVariantGroupIds: string[];
    AIGeneratedVariantGroupIds: string[];
} {
    const AIOrganizedVariantScreenIds = [
        ...newVariantGroups.flatMap(variantGroup => getScreenIdsFromAIOrganizedVariantGroup(variantGroup)),
        ...updatedVariantGroups.flatMap(variantGroup => getScreenIdsFromAIOrganizedVariantGroup(variantGroup))
    ];

    const AIOrganizedVariantScreenIdsSet = new Set(AIOrganizedVariantScreenIds);
    const updatedVariantGroupMap = new Map(updatedVariantGroups.map(variantGroup => [variantGroup.id, variantGroup]));

    const AIGeneratedVariantGroups: VariantGroup[] = newVariantGroups.map(variantGroup => ({
        _id: variantGroup.id,
        name: variantGroup.name,
        screens: variantGroup.screens
    }));

    const computedVariantGroups = variantGroups.map(variantGroup => {
        const filteredScreens = variantGroup.screens.filter(screen => !AIOrganizedVariantScreenIdsSet.has(screen.id));
        const updatedVariantGroup = updatedVariantGroupMap.get(variantGroup._id);

        return {
            ...variantGroup,
            screens: updatedVariantGroup
                ? [...filteredScreens, ...updatedVariantGroup.screens]
                : filteredScreens
        };
    });

    computedVariantGroups.push(...AIGeneratedVariantGroups);

    const AIOrganizedScreenIds = [
        ...newSections.flatMap(section => getScreenIdsFromAIOrganizedSection(section, computedVariantGroups)),
        ...updatedSections.flatMap(section => {
            const sectionWithFilteredItems = {
                ...section,
                items: section.items?.filter(item => item.new)
            };

            return getScreenIdsFromAIOrganizedSection(sectionWithFilteredItems, computedVariantGroups);
        })
    ];
    const AIOrganizedScreenIdsSet = new Set(AIOrganizedScreenIds);
    const updatedSectionsMap = new Map(updatedSections.map(s => [s.id, s]));
    const AIGeneratedSections = newSections.map(section => ({
        _id: section.id,
        name: section.name,
        description: section.description,
        order: section.order,
        parent: section.parent,
        screens: getScreenIdsFromAIOrganizedSection(section, computedVariantGroups)
    }));

    const computedSections = sections.map(section => {
        const filteredScreens = section.screens.filter(screenId => !AIOrganizedScreenIdsSet.has(screenId));
        const updatedSection = updatedSectionsMap.get(section._id);
        const computedUpdatedSection = {
            ...section,
            screens: filteredScreens
        };

        if (updatedSection) {
            computedUpdatedSection.screens = getScreenIdsFromAIOrganizedSection(updatedSection, computedVariantGroups);

            if (updatedSection.name) {
                computedUpdatedSection.name = updatedSection.name;
            }

            if (updatedSection.description) {
                computedUpdatedSection.description = updatedSection.description;
            }

            if (updatedSection.order) {
                computedUpdatedSection.order = updatedSection.order;
            }

            if (Object.prototype.hasOwnProperty.call(updatedSection, "parent")) {
                computedUpdatedSection.parent = updatedSection.parent;

                if (!updatedSection.parent) {
                    delete computedUpdatedSection.path;
                }
            }
        }

        return computedUpdatedSection;
    });

    computedSections.push(...AIGeneratedSections);

    const AIGeneratedSectionIds = AIGeneratedSections.map(section => section._id);
    const updatedSectionIds = updatedSections.map(section => section.id);
    const AIGeneratedVariantGroupIds = AIGeneratedVariantGroups.map(variantGroup => variantGroup._id);
    const updatedVariantGroupIds = updatedVariantGroups.map(variantGroup => variantGroup.id);

    computedSections.sort((a, b) => {
        if (a.order! < b.order!) {
            return -1;
        } else if (a.order! > b.order!) {
            return 1;
        }

        return 0;
    });

    return {
        sections: transformScreenSections(computedSections) as unknown as ScreenSection[],
        variantGroups: computedVariantGroups,
        AIOrganizedScreenIds,
        AIOrganizedSectionIds: [...updatedSectionIds, ...AIGeneratedSectionIds],
        AIGeneratedSectionIds,
        AIOrganizedVariantGroupIds: [...updatedVariantGroupIds, ...AIGeneratedVariantGroupIds],
        AIGeneratedVariantGroupIds
    };
}

function findBarrelOrganizationId(bid: string | null | undefined): string | null {
    const barrel = getBarrel({ bid });

    if (!barrel) {
        return null;
    }

    const { organizationProjects, organizationStyleguides } = fluxRuntime.OrganizationStore.getState();

    for (const [oid, pids] of Object.entries(organizationProjects)) {
        if (pids.includes(barrel._id)) {
            return oid;
        }
    }

    for (const [oid, stids] of Object.entries(organizationStyleguides)) {
        if (stids.includes(barrel._id)) {
            return oid;
        }
    }

    return null;
}

function getWorkspaceURL({
    oid,
    barrelType: paramBarrelType,
    query
}: NavigateToWorkspaceParams = {}): string {
    // Passing oid is required when BarrelStore is reset, otherwise navigates to personal WS
    if (oid) {
        const resetWorkspaceBarrelType = paramBarrelType ?? BarrelType.PROJECT;
        const resetWorkspaceSection = resetWorkspaceBarrelType === BarrelType.PROJECT ? "projects" : "styleguides";
        return oid === "user"
            ? urlResolve(resetWorkspaceSection, { query })
            : urlResolve(`workspace-${resetWorkspaceSection}`, { url: { oid }, query });
    }

    const {
        organizationId,
        barrelType
    } = fluxRuntime.BarrelStore.getState();

    const barrelUser = findUser();

    const workspaceSectionBarrelType = paramBarrelType ?? barrelType;
    const workspaceSection = workspaceSectionBarrelType === BarrelType.PROJECT ? "projects" : "styleguides";

    const workspaceId = organizationId ?? "user";

    return workspaceId === "user" || !barrelUser
        ? urlResolve(workspaceSection, { query })
        : urlResolve(`workspace-${workspaceSection}`, { url: { oid: workspaceId }, query });
}

function navigateToWorkspace(params: NavigateToWorkspaceParams = {}) {
    const workspaceURL = getWorkspaceURL(params);

    setTimeout(() => router.history.push(workspaceURL), 4);
}

/* TODO: This helper is used in some mappers, and it returns a new array reference each call.
*  We should find a way to memoize it's returned reference if nothing changed in this
*  function's dependencies(BarrelStore, StyleguidesDataStore, and ProjectsDataStore)
*/
function getStyleguidesData(): StyleguideData[] {
    const { loading, ...barrelState } = fluxRuntime.BarrelStore.getState();

    if (loading) {
        return [];
    }

    const { styleguides } = fluxRuntime.StyleguidesDataStore.getState();
    const barrel = getBarrel()!;
    const platform = getPlatform();
    const barrelUser = findUser();
    const {
        barrelId,
        barrelType
    } = barrelState;

    const {
        name: barrelName,
        description,
        colors,
        textStyles,
        spacingSections,
        remPreferences,
        componentSections,
        connectedComponents,
        localConnectedComponents,
        densityScale,
        variableCollections,
        previewBackgroundColor
    } = barrel;
    const { dimensionSuffix, textDimensionSuffix } = PlatformContextMap[platform];

    if (!barrelUser) {
        return [];
    }

    const barrelUserId = barrelUser.user._id;
    const isMemberOfBarrel = isUserMemberOfBarrel(barrel, barrelUserId);

    if (!isMemberOfBarrel) {
        return [];
    }

    const localStyleguideName = barrelType === BarrelType.STYLEGUIDE ? barrelName : "Local styleguide";

    const linkedStyleguides = getLinkedStyleguideIds(barrel, barrelType)
        .map(ancestor => styleguides[ancestor])
        .filter(Boolean);

    const localStyleguide = {
        id: barrelId!,
        barrelType,
        name: localStyleguideName,
        description,
        platform,
        colors,
        textStyles,
        spacingSections: spacingSections?.map(spacingSection => ({
            ...spacingSection,
            spacingTokens: sortArrayOfObjectsByPropertyName(spacingSection.spacingTokens, "value")
        })) ?? [],
        remPreferences: getRemPreferences({ bid: barrelId }),
        parentStyleguideToInheritRemPreferences:
            getParentStyleguideToInheritRemPreferences(remPreferences, linkedStyleguides),
        componentSections,
        connectedComponents,
        localConnectedComponents,
        isMember: isMemberOfBarrel,
        isOrganizationBarrel: isOrganizationProject(barrel),
        dimensionSuffix,
        textDimensionSuffix,
        densityScale,
        variableCollections,
        previewBackgroundColor
    };

    const linkedStyleguidesData = linkedStyleguides.map(styleguide => {
        const linkedStyleguidePlatform = getPlatform({ bid: styleguide._id });
        const linkedStyleguidePlatformContext = PlatformContextMap[platform];

        return ({
            id: styleguide._id,
            barrelType: BarrelType.STYLEGUIDE,
            name: styleguide.name,
            description: styleguide.description,
            platform: linkedStyleguidePlatform,
            colors: styleguide.colors,
            textStyles: styleguide.textStyles,
            spacingSections: styleguide.spacingSections?.map(spacingSection => ({
                ...spacingSection,
                spacingTokens: sortArrayOfObjectsByPropertyName(spacingSection.spacingTokens, "value")
            })) ?? [],
            remPreferences: getRemPreferences({ bid: styleguide._id }),
            componentSections: styleguide.componentSections,
            connectedComponents: styleguide.connectedComponents,
            localConnectedComponents: styleguide.localConnectedComponents,
            isMember: isUserMemberOfBarrel(styleguide, barrelUserId),
            isOrganizationBarrel: isOrganizationProject(styleguide),
            dimensionSuffix: linkedStyleguidePlatformContext.dimensionSuffix,
            textDimensionSuffix: linkedStyleguidePlatformContext.textDimensionSuffix,
            densityScale: styleguide.densityScale,
            variableCollections: styleguide.variableCollections,
            previewBackgroundColor: styleguide.previewBackgroundColor
        });
    });

    return [localStyleguide, ...linkedStyleguidesData];
}

function navigateToBarrel(barrelType: BarrelType, bid: string) {
    const barrelURL = urlResolve(barrelType, { url: { bid } });
    router.history.push(barrelURL);
}

function reloadCurrentPage() {
    const { href: redirectUrl } = window.location;

    setTimeout(() => {
        router.history.replace(redirectUrl);
    }, 4);
}

function getNoteTypes(pid: string | null | undefined): NoteType[] {
    const project = getProject({ pid });

    const { noteTypes: projectNoteTypes = [] } = project ?? {};

    const organizationId = findBarrelOrganizationId(pid);
    const { noteTypes: organizationNoteTypes = [] } =
        OrganizationHelpers.getOrganization(organizationId) ?? {};

    return projectNoteTypes.concat(organizationNoteTypes);
}

function findNoteType(pid: string, ntid?: string): NoteType {
    if (!ntid) {
        return NoteType.None;
    }

    const noteTypes = getNoteTypes(pid);
    return noteTypes.find(noteType => noteType._id === ntid) || NoteType.None;
}

function findNoteTypeByName(pid: string, name: string): NoteType {
    if (!name) {
        return NoteType.None;
    }

    const noteTypes = getNoteTypes(pid);
    return noteTypes.find(noteType => noteType.name === name) || NoteType.None;
}

function noteTypeExists(pid: string, ntid?: string): boolean {
    if (!ntid) {
        return false;
    }

    const noteTypes = getNoteTypes(pid);
    return noteTypes.some(noteType => noteType._id === ntid);
}

function isOnboardingProject(project?: Barrel | ApiBarrel | null): boolean {
    let currentProject = project;

    if (!currentProject) {
        currentProject = getProject();
    }

    if (!currentProject) {
        return false;
    }

    return currentProject.kind === BarrelKind.Onboarding;
}

function onboardingProjectExists(): boolean {
    const { projects } = fluxRuntime.ProjectsDataStore.getState();

    return Object.values(projects).some(project => isOnboardingProject(project));
}

function shouldAutoFollowEmailNotifications({ projectId }: {
    projectId: string;
}) {
    const { emailNotificationSettings } = fluxRuntime.UserStore.getState();
    const { followedProjects, unfollowedProjects } = emailNotificationSettings!;

    const alreadyFollowing = followedProjects.has(projectId);
    const alreadyUnfollowed = unfollowedProjects.has(projectId);
    return !alreadyFollowing && !alreadyUnfollowed;
}

function isVersionDiffEnabled(bid: string) {
    const barrel = getBarrel({ bid });

    if (!barrel) {
        return false;
    }

    return isOrganizationProject(barrel);
}

function getPlatform({ bid }: { bid?: string | null; } = {}): PLATFORM {
    const barrel = getBarrel({ bid });
    const { platform } = fluxRuntime.UserStore.getState();
    const renderablePlatforms: PLATFORM[] = Object.values(PlatformContextMap)
        .filter(({ isDeprecated }) => !isDeprecated)
        .map(({ id }) => id);
    const computedPlatform = (platform || barrel?.type) as PLATFORM;

    return renderablePlatforms.includes(computedPlatform) ? computedPlatform : PLATFORM.WEB;
}

function getDuplicateBarrelNameMap(): BasicRecord<boolean> {
    const { selectedTab } = fluxRuntime.SpaceStore.getState();
    let barrelsArray: Barrel[] = [];

    if (selectedTab === WorkspaceTabs.PROJECTS) {
        const { projects } = fluxRuntime.ProjectsDataStore.getState();

        barrelsArray = Object.values(projects);
    } else if (selectedTab === WorkspaceTabs.STYLEGUIDES) {
        const { styleguides } = fluxRuntime.StyleguidesDataStore.getState();

        barrelsArray = Object.values(styleguides);
    }

    const nameCounts: BasicRecord<number> = barrelsArray.reduce((acc, item) => {
        acc[item.name] = (acc[item.name] || 0) + 1;
        return acc;
    }, {} as BasicRecord<number>);

    return barrelsArray.reduce((acc, item) => {
        acc[item._id] = nameCounts[item.name] > 1;
        return acc;
    }, {} as BasicRecord<boolean>);
}

function getRemPreferences({ bid }: { bid?: string | null; } = {}): RemPreferences {
    const { styleguides } = fluxRuntime.StyleguidesDataStore.getState();
    const { barrelType, barrel } = getBarrelAndType({ bid })!;
    const { remPreferences } = barrel;

    let linkedRemPreferences;

    if (!remPreferences.status || remPreferences.status === "linked") {
        const linkedStyleguides = getLinkedStyleguideIds(barrel, barrelType)
            .map(ancestor => styleguides[ancestor])
            .filter(Boolean);

        const styleguide = linkedStyleguides.find(
            linkedStyleguide => linkedStyleguide.remPreferences.status && linkedStyleguide.remPreferences.status !== "linked"
        );

        if (styleguide) {
            linkedRemPreferences = styleguide.remPreferences;
        }
    }

    return linkedRemPreferences || remPreferences;
}

function getSplitSectionData({
    sid,
    isSubsection,
    sections
}: {
    sid: string;
    isSubsection: boolean;
    sections?: ScreenSection[];
}) {
    let computedSections = sections;

    if (!computedSections) {
        const barrel = getBarrel()!;

        computedSections = barrel.sections;
    }

    const sourceSection = computedSections!.find(({ screens }) => screens.includes(sid))!;
    const screenIndex = sourceSection.screens.indexOf(sid);
    const sourceSectionIndex = computedSections!.findIndex(({ _id }) => _id === sourceSection._id);
    const screens = sourceSection.screens.slice(screenIndex);
    const _id = createTemporarySectionId(TemporarySectionId.NewSectionFromSplit);
    const section: ScreenSection = {
        _id,
        name: "Untitled section",
        screens
    };
    let index = sourceSectionIndex + 1;

    if (isSubsection) {
        section.parent = sourceSection._id;
        section.path = [...(sourceSection.path || []), sourceSection._id];
    } else {
        const sourceSectionChildCount = computedSections!
            .filter(({ path }) => (path || []).includes(sourceSection._id)).length;

        index += sourceSectionChildCount;

        if (sourceSection.parent) {
            section.parent = sourceSection.parent;
            section.path = sourceSection.path;
        }
    }

    return {
        section,
        index,
        sourceSection
    };
}

function setOrderForProjectAIOrganizeNewSections(
    bid: string,
    newSections: ProjectAIOrganizeResultSuggestionNewSection[]
) {
    const barrel = getBarrel({ bid });

    if (barrel) {
        const { sections = [] } = barrel;
        const newOrders = generateNKeysBetween(
            sections[0]?.order,
            sections[1]?.order,
            newSections.length
        );

        newSections.forEach((section, i) => {
            section.order = newOrders[i];
        });
    }

    return newSections;
}

function getProjectAIOrganizeSectionItems({
    screenIds,
    variantGroupsMapping,
    shouldMarkAsNew = false
}: {
    screenIds: string[];
    variantGroupsMapping: BasicRecord<VariantGroupData>;
    shouldMarkAsNew?: boolean;
}): ProjectAIOrganizeResultSuggestionItem[] {
    const projectAIOrganizedSectionItems: ProjectAIOrganizeResultSuggestionItem[] = [];

    for (const screenId of screenIds) {
        const variantGroup = variantGroupsMapping[screenId];

        if (variantGroup) {
            projectAIOrganizedSectionItems.push({
                id: variantGroup.variantGroupId,
                type: "variantGroup",
                new: shouldMarkAsNew
            });
        } else {
            projectAIOrganizedSectionItems.push({
                id: screenId,
                type: "screen",
                new: shouldMarkAsNew
            });
        }
    }

    return projectAIOrganizedSectionItems;
}

export default {
    hasProject,
    hasStyleguide,
    getBarrel,
    getProject,
    findUser,
    isUserRestricted,
    getVariantGroupOfScreen,
    getAIOrganizedProjectSections,
    findBarrelOrganizationId,
    getWorkspaceURL,
    getStyleguidesData,
    navigateToWorkspace,
    canUploadDesigns,
    navigateToBarrel,
    reloadCurrentPage,
    getNoteTypes,
    findNoteType,
    findNoteTypeByName,
    noteTypeExists,
    isOnboardingProject,
    onboardingProjectExists,
    shouldAutoFollowEmailNotifications,
    isVersionDiffEnabled,
    getPlatform,
    getDuplicateBarrelNameMap,
    getRemPreferences,
    getSplitSectionData,
    setOrderForProjectAIOrganizeNewSections,
    getProjectAIOrganizeSectionItems
};
